import { useState } from "react";
import { Building2, Briefcase } from "lucide-react";
import JourneyDiagram from "./JourneyDiagram";

const SEEKER_COLOR = "#7C3AED"; // violet
const AGENCY_COLOR = "#EC4899"; // magenta
const EMPLOYER_COLOR = "#0EA5E9"; // cyan-blue

const FLOWS = {
  agency: {
    title: "With an agency",
    description:
      "Recruitment agencies use Joborigo to manage candidate pipelines, share curated shortlists with clients, and push status updates back to candidates. Candidates stay informed in real-time, agencies stay in control of their relationships.",
    icon: Building2,
    lanes: [
      { id: "seeker", label: "SEEKER", color: SEEKER_COLOR },
      { id: "agency", label: "AGENCY", color: AGENCY_COLOR },
      { id: "employer", label: "EMPLOYER", color: EMPLOYER_COLOR },
    ],
    phases: [
      { id: "apply", label: "Apply", sublabel: "Submit resume" },
      { id: "pipeline", label: "Pipeline", sublabel: "Status tracked" },
      { id: "share", label: "Client share", sublabel: "Curated shortlist" },
      { id: "interview", label: "Interview", sublabel: "Direct contact" },
      { id: "decision", label: "Decision", sublabel: "Outcome pushed" },
    ],
    // [phaseIdx][laneIdx]
    activity: [
      [true, true, false], // Apply
      [true, true, false], // Pipeline
      [false, true, true], // Client share
      [true, true, true], // Interview
      [true, true, true], // Decision
    ],
  },
  employer: {
    title: "Direct with employer",
    description:
      "When you apply directly to a company, employers use Joborigo to track candidates and push status updates in real-time. No agency in the middle — just transparent communication between you and the hiring team.",
    icon: Briefcase,
    lanes: [
      { id: "seeker", label: "SEEKER", color: SEEKER_COLOR },
      { id: "employer", label: "EMPLOYER", color: EMPLOYER_COLOR },
    ],
    phases: [
      { id: "apply", label: "Apply", sublabel: "Submit resume" },
      { id: "pipeline", label: "Pipeline", sublabel: "Status tracked" },
      { id: "interview", label: "Interview", sublabel: "Direct contact" },
      { id: "decision", label: "Decision", sublabel: "Outcome pushed" },
    ],
    activity: [
      [true, true], // Apply
      [true, true], // Pipeline
      [true, true], // Interview
      [true, true], // Decision
    ],
  },
};

export default function HowJoborigoWorks() {
  const [activeFlow, setActiveFlow] = useState("agency");
  const flow = FLOWS[activeFlow];
  const Icon = flow.icon;

  return (
    <section className="mb-16">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
          How Joborigo works
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          Whether your application goes through an agency or directly to an
          employer, Joborigo keeps everyone informed.
        </p>
      </div>

      {/* Tab toggle */}
      <div className="flex justify-center gap-2 mb-8">
        {Object.entries(FLOWS).map(([key, f]) => {
          const TabIcon = f.icon;
          const isActive = activeFlow === key;
          return (
            <button
              key={key}
              onClick={() => setActiveFlow(key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition ${
                isActive
                  ? "bg-brand-violet text-white"
                  : "bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10"
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {f.title}
            </button>
          );
        })}
      </div>

      {/* Diagram + description */}
      <div className="rounded-2xl bg-white dark:bg-brand-dark-surface border border-gray-200 dark:border-white/10 p-6 md:p-10">
        <div className="mb-6">
          <JourneyDiagram
            lanes={flow.lanes}
            phases={flow.phases}
            activity={flow.activity}
          />
        </div>

        <div className="flex items-start gap-3 pt-6 border-t border-gray-100 dark:border-white/10">
          <div className="w-9 h-9 rounded-lg bg-brand-violet-tint dark:bg-brand-violet/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-brand-violet dark:text-brand-violet-on-dark" />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {flow.description}
          </p>
        </div>
      </div>
    </section>
  );
}
